import {RouteConfig} from 'vue-router'
import {MenuItem} from '@/store/types'
import {MediaUpload} from '@/modules/media/types'

/**
 * Evento lanzado por el administrador de subidas cuando es completada
 * una cola de subida
 */
export interface UploadCompletedEvent {
    result: 'OK' | 'FAIL'
    uploads: MediaUpload[]
}

/**
 * Declara los elementos para el menu de navegación de la aplicación
 */
export function menu (): MenuItem[] {
    return [
        {
            title: 'Reservas',
            icon: 'mdi-book-variant',
            requireAuthorization: true,
            permission: '@/reservations/admin',
            children: [
                {
                    title: 'Departamentos',
                    icon: 'mdi-soccer-field',
                    routeName: 'reservations.department-list',
                    requireAuthorization: true,
                    permission: '@/reservations/admin/departments',
                },
                {
                    title: 'Grilla de reservas',
                    icon: 'mdi-grid',
                    routeName: 'reservation.grid',
                    requireAuthorization: true,
                    permission: '@/reservations/admin/reservation',
                },
                {
                    title: 'Lista de reservas',
                    icon: 'mdi-format-list-bulleted-square',
                    routeName: 'reservations.reservation-list',
                    requireAuthorization: true,
                    permission: '@/reservations/admin/reservation',
                },
                {
                    title: 'Creador de reservas',
                    icon: 'mdi-book-multiple',
                    routeName: 'reservations.bulk-creator',
                    requireAuthorization: true,
                    permission: '@/reservations/admin/reservation',
                },
                {
                    title: 'Tipos de departamentos',
                    module: 'reservations.department-types',
                    icon: 'mdi-rhombus-outline',
                    routeName: 'reservations.department-types',
                    requireAuthorization: true,
                    permission: '@/reservations/admin/department-types',
                },
                {
                    title: 'Servicios de departamentos',
                    module: 'reservations.servicios',
                    icon: 'mdi-lightbulb',
                    routeName: 'reservations.services',
                    requireAuthorization: true,
                    permission: '@/reservations/admin/services',
                },
                {
                    title: 'Eventos',
                    module: 'reservations.event-list',
                    icon: 'mdi-calendar',
                    routeName: 'reservations.event-list',
                    requireAuthorization: true,
                    permission: '@/reservations/admin/departments',
                },
            ],
        },
    ]
}

/**
 * Declara las rutas disponibles
 */
export function routes (): RouteConfig[] {
    return [
        {
            name: 'reservations.reservation-list',
            path: '/reserva',
            component: () => import('@/modules/reservation/components/ReservationList.vue'),
            meta: {
                toolbarTitle: 'Lista de reservas',
                authRequired: true,
                vuex: {
                    loadModules: ['reservation', 'user'],
                },
            },
            children: [
                {
                    name: 'reservations.reservation-detail',
                    path: ':id',
                    component: () => import('@/modules/reservation/components/ReservationDetail.vue'),
                    meta: {
                        toolbarTitle: 'Lista de reservas',
                        authRequired: true,
                        vuex: {
                            loadModules: ['reservation', 'user'],
                        },
                    },
                },
            ],
        },
        {
            name: 'reservations.department-list',
            path: '/reservas/departamentos',
            component: () => import('@/modules/reservation/components/DepartmentList.vue'),
            meta: {
                toolbarTitle: 'Lista de departamentos',
                authRequired: true,
                vuex: {
                    loadModules: ['reservation'],
                },
            },
        },
        {
            name: 'reservations.department-detail',
            path: '/reservas/departamentos/:id',
            component: () => import('@/modules/reservation/components/DepartmentDetail.vue'),
            meta: {
                authRequired: true,
                toolbarTitle: 'Ver/Editar departamento',
                vuex: {
                    loadModules: ['reservation', 'media'],
                },
            },
        },
        {
            name: 'reservations.department-types',
            path: '/reservas/tipos-de-departamentos',
            component: () => import('@/modules/reservation/components/DepartmentTypeList.vue'),
            meta: {
                authRequired: true,
                toolbarTitle: 'Lista de tipos de departamentos',
                vuex: {
                    loadModules: ['reservation', 'product'],
                },
            },
        },
        {
            name: 'reservations.services',
            path: '/reservas/servicios',
            component: () => import('@/modules/reservation/components/ServicesList.vue'),
            meta: {
                authRequired: true,
                toolbarTitle: 'Lista de servicios',
                vuex: {
                    loadModules: ['reservation', 'product'],
                },
            },
        },
        {
            name: 'reservations.event-list',
            path: '/reservas/eventos',
            component: () => import('@/modules/reservation/components/EventList.vue'),
            meta: {
                authRequired: true,
                toolbarTitle: 'Lista de eventos',
                vuex: {
                    loadModules: ['reservation'],
                },
            },
        },
        {
            name: 'reservations.bulk-creator',
            path: '/reservas/creador',
            component: () => import('@/modules/reservation/components/BulkReservationCreator.vue'),
            meta: {
                authRequired: true,
                toolbarTitle: 'Creador de reservas',
                vuex: {
                    loadModules: ['reservation', 'user'],
                },
            },
        },
        {
            name: 'reservation.grid',
            path: '/reservas/grilla',
            component: () => import('@/modules/reservation/components/ReservationGrid.vue'),
            children: [
                {
                    name: 'reservation.grid.detail',
                    path: 'reserva/:id',
                    component: () => import('@/modules/reservation/components/ReservationDetail.vue'),
                    props: {
                      backTo: {name: 'reservation.grid'},
                    },
                    meta: {
                        authRequired: true,
                        toolbarTitle: 'Ver/Editar reservas',
                        vuex: {
                            loadModules: ['reservation', 'user'],
                        },
                    },
                },
            ],
            meta: {
                authRequired: true,
                toolbarTitle: 'Grilla de reservas',
                vuex: {
                    loadModules: ['reservation', 'user'],
                },
            },
        },
    ]
}
